// Shared display formatters for schema-driven rendering.
//
// One formatter path. Every surface that shows the same field must call
// the same function here — the compare table, the SelectedSiteCard and
// (for the headline fields covered by parity tests) AnalysisPanel.
//
// Nulls render as `NOT_ASSESSED` or `NO_DATA` per the schema copy rule.
// Never 0, "—" or "Unknown" for a null upstream value.

export const NOT_ASSESSED = 'Not assessed';
export const NO_DATA = 'No data';

// Distance in kilometres → "425 m" / "2.3 km" / "155 km".
// Bucket rules (mirrored exactly by distanceKey):
//   - metres bucket only when rounded m < 1000 (so 0.9995 km promotes
//     to "1.0 km", not "1000 m").
//   - tenths-of-km bucket only when rounded tenths < 100 (so 9.95 km
//     promotes to "10 km", not "10.0 km").
//   - integer km otherwise.
// Whole months since an ISO date (YYYY-MM-DD…), or null.
export function monthsSince(iso) {
  if (!iso) return null;
  const t = Date.parse(String(iso).slice(0, 10));
  if (!Number.isFinite(t)) return null;
  return Math.floor((Date.now() - t) / (30.44 * 24 * 3600 * 1000));
}

export function formatDistanceKm(km) {
  if (km == null || !Number.isFinite(Number(km))) return null;
  const n = Number(km);
  if (n < 1) {
    const m = Math.round(n * 1000);
    if (m < 1000) return `${m} m`;
  }
  if (n < 10) {
    const tenths = Math.round(n * 10);
    if (tenths < 100) return `${(tenths / 10).toFixed(1)} km`;
  }
  return `${Math.round(n)} km`;
}

// Comparison key that groups values displaying identically. Mirrors
// formatDistanceKm exactly — two values with the same display return the
// same key. Property test in displaySchema.behaviour.test.jsx enforces this.
export function distanceKey(km) {
  if (km == null || !Number.isFinite(Number(km))) return null;
  const n = Number(km);
  if (n < 1) {
    const m = Math.round(n * 1000);
    if (m < 1000) return m / 1000;              // 3 dp = per metre
  }
  if (n < 10) {
    const tenths = Math.round(n * 10);
    if (tenths < 100) return tenths / 10;       // 1 dp = per 0.1 km
  }
  return Math.round(n);                          // integer km
}

// Distance in metres → same buckets, converts internally
export function formatDistanceM(m) {
  if (m == null || !Number.isFinite(Number(m))) return null;
  return formatDistanceKm(Number(m) / 1000);
}

// Nearest home from the screen's facts. The screen cuts the land at the
// brief's home limit and then measures from the land's edge, so a site that
// touches a cut reads the limit itself (500–503 m with the buffer polygon):
// "At least 500 m" is what that measures, not "500 m" (2026-10-13 review).
export function nearestHomeText(m, limit) {
  if (m == null || !Number.isFinite(Number(m))) return { text: 'None within 5 km', atLimit: false };
  const lim = limit == null || !Number.isFinite(Number(limit)) ? null : Number(limit);
  if (lim != null && Number(m) <= lim + 5) return { text: `At least ${lim} m`, atLimit: true };
  return { text: formatDistanceM(m), atLimit: false };
}

// Area in hectares → "3.2 ha" / "14 ha".
// Bucket promotes to integer when rounded tenths would show as ".0"
// (so 9.95 → "10 ha", not "10.0 ha"); mirrored by areaKey.
export function formatAreaHa(ha) {
  if (ha == null || !Number.isFinite(Number(ha))) return null;
  const n = Number(ha);
  if (n < 10) {
    const tenths = Math.round(n * 10);
    if (tenths < 100) return `${(tenths / 10).toFixed(1)} ha`;
  }
  return `${Math.round(n)} ha`;
}

export function areaKey(ha) {
  if (ha == null || !Number.isFinite(Number(ha))) return null;
  const n = Number(ha);
  if (n < 10) {
    const tenths = Math.round(n * 10);
    if (tenths < 100) return tenths / 10;
  }
  return Math.round(n);
}

// Humanize substation names. OSM-derived rows without a `name` tag arrive
// as raw identifiers ("OSM way/911006998", "way/12345", "node/…"); the
// display schema at vanir_display_schema.yaml explicitly says these should
// render as "Unnamed substation" rather than leaking the ID. Anything
// with a real name passes through unchanged.
const OSM_ID_RE = /^(?:OSM\s+)?(?:substation\s+)?(?:way|node|relation)\/\d+$/i;
export function humanizeSubstationName(name) {
  if (name == null) return 'Unnamed substation';
  const s = String(name).trim();
  if (!s) return 'Unnamed substation';
  if (OSM_ID_RE.test(s)) return 'Unnamed substation';
  return s;
}

// Voltage → "110 kV"
export function formatVoltageKv(kv) {
  if (kv == null || !Number.isFinite(Number(kv))) return null;
  return `${Number(kv)} kV`;
}

// Percentage → whole numbers, e.g. "2%" or "12%"
export function formatPercent(v, { fractionDigits = 0 } = {}) {
  if (v == null || !Number.isFinite(Number(v))) return null;
  return `${Number(v).toFixed(fractionDigits)}%`;
}

// Coordinates → "62.8401, 22.9508"
export function formatCoords(lat, lon, { fractionDigits = 4 } = {}) {
  if (lat == null || lon == null) return null;
  return `${Number(lat).toFixed(fractionDigits)}, ${Number(lon).toFixed(fractionDigits)}`;
}

// Elevation → "59 m"
export function formatElevationM(m) {
  if (m == null || !Number.isFinite(Number(m))) return null;
  return `${Math.round(Number(m))} m`;
}

// Temperature range → "-32°C / +29°C"
export function formatTempRange(min, max) {
  if (min == null && max == null) return null;
  const l = min != null ? `${Math.round(Number(min))}°C` : '?';
  const r = max != null ? (Number(max) >= 0 ? `+${Math.round(Number(max))}°C` : `${Math.round(Number(max))}°C`) : '?';
  return `${l} / ${r}`;
}

// Read a dot-path from an object. Used by the schema fields to pull values.
// Returns undefined if any segment is missing.
export function readPath(obj, path) {
  if (obj == null) return undefined;
  if (!path) return obj;
  const parts = path.split('.');
  let cur = obj;
  for (const p of parts) {
    if (cur == null) return undefined;
    cur = cur[p];
  }
  return cur;
}

// Convenience: pull a value out of the project's enrichment blob.
// Passing 'lat' or 'lon' returns the top-level project field.
const TOPLEVEL_KEYS = new Set(['lat', 'lon', 'country', 'site_label', 'readiness_score', 'readiness_rating', 'id']);
export function projectValue(project, path) {
  if (!project) return undefined;
  const first = path.split('.')[0];
  if (TOPLEVEL_KEYS.has(first)) return readPath(project, path);
  return readPath(project.enrichment || {}, path);
}
